import { useState, useCallback, useRef } from 'react';
import { TypesenseService } from '../lib/typesense/TypesenseService';
import { TypesenseUser } from '../lib/typesense/types';

export interface UseTypesenseSearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
  maxResults?: number;
}

export function useTypesenseSearch(options: UseTypesenseSearchOptions = {}) {
  const {
    debounceMs = 150,
    minQueryLength = 2,
    maxResults = 10,
  } = options;

  const [users, setUsers] = useState<TypesenseUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const serviceRef = useRef<TypesenseService | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const getService = useCallback(() => {
    if (!serviceRef.current) {
      serviceRef.current = new TypesenseService();
    }
    return serviceRef.current;
  }, []);

  const search = useCallback(async (query: string) => {
    if (query.length < minQueryLength) {
      setUsers([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const service = getService();
      const results = await service.searchUsers(query, maxResults);
      setUsers(results);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Search failed';
      setError(errorMessage);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [getService, minQueryLength, maxResults]);

  const debouncedSearch = useCallback((query: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      search(query);
    }, debounceMs);
  }, [search, debounceMs]);

  const clearResults = useCallback(() => {
    setUsers([]);
    setError(null);
    setIsLoading(false);
  }, []);

  const clearCache = useCallback(() => {
    const service = getService();
    service.clearCache();
  }, [getService]);

  return {
    users,
    isLoading,
    error,
    search: debouncedSearch,
    clearResults,
    clearCache,
  };
}
